import { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { shell } from './strings'
import './ConsoleLogPanel.css'

const LEVELS = ['log', 'info', 'warn', 'error']
const MAX_ENTRIES = 300
const PATCH_FLAG = '__rawamConsolePatched'

/** Filename:line of the first frame outside this module, as the legacy panel does. */
function callerLocation() {
  try {
    const lines = new Error().stack?.split('\n').map((line) => line.trim())
    if (!lines) return 'unknown'
    for (let i = 2; i < lines.length; i += 1) {
      const match = lines[i].match(/(https?:\/\/[^\s)]+):(\d+):(\d+)/)
      if (match && !lines[i].includes('ConsoleLogPanel')) {
        return `${match[1].split('/').pop()}:${match[2]}`
      }
    }
    return 'unknown'
  } catch {
    return 'unknown'
  }
}

const format = (value) => {
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

/**
 * The floating console mirror from Views/Shared/ConsoleLogs.cshtml — opened from the
 * account menu (`showConsoleLogs()`) or with F9, draggable by its header, closed with
 * the ✖. It patches console.log/info/warn/error, keeps the original behaviour, and
 * tags every line with the caller's file:line. LTR and monospace, like the original.
 */
export default function ConsoleLogPanel({ open, onOpenChange }) {
  const [entries, setEntries] = useState([])
  const [position, setPosition] = useState({ x: 24, y: 24 })
  const panelRef = useRef(null)
  const bodyRef = useRef(null)
  const dragRef = useRef(null)
  const bufferRef = useRef([])
  const flushRef = useRef(0)

  // Patch the console once, for the lifetime of the app — the legacy partial does
  // this at page load whether or not the panel is visible.
  //
  // Entries are buffered and flushed on a timer rather than written straight to
  // state: a `console.log` in a render body would otherwise setState during another
  // component's render, and React's own warning about that goes through the patched
  // `console.error`, feeding itself.
  useEffect(() => {
    const originals = {}

    LEVELS.forEach((level) => {
      const original = console[level]
      // A second instance (or an HMR reload) must not capture an already-patched
      // function as "the original" and leave the chain permanently wrapped.
      if (original[PATCH_FLAG]) return

      originals[level] = original

      const patched = (...args) => {
        bufferRef.current.push({
          level,
          text: args.map(format).join(' '),
          location: callerLocation(),
        })
        if (!flushRef.current) {
          flushRef.current = setTimeout(() => {
            flushRef.current = 0
            const pending = bufferRef.current
            bufferRef.current = []
            setEntries((previous) => [...previous, ...pending].slice(-MAX_ENTRIES))
          }, 0)
        }
        original.apply(console, args)
      }
      patched[PATCH_FLAG] = true
      console[level] = patched
    })

    return () => {
      clearTimeout(flushRef.current)
      flushRef.current = 0
      Object.entries(originals).forEach(([level, original]) => {
        console[level] = original
      })
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'F9') {
        event.preventDefault()
        onOpenChange(true)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onOpenChange])

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight
  }, [entries, open])

  const onPointerMove = useCallback((event) => {
    if (!dragRef.current) return
    const box = panelRef.current?.getBoundingClientRect()
    const maxX = window.innerWidth - (box?.width ?? 0)
    const maxY = window.innerHeight - 32
    // Clamped, or the panel can be dragged somewhere with no grabbable header and
    // reopens there on the next F9.
    setPosition({
      x: Math.min(Math.max(0, event.clientX - dragRef.current.x), Math.max(0, maxX)),
      y: Math.min(Math.max(0, event.clientY - dragRef.current.y), Math.max(0, maxY)),
    })
  }, [])

  const endDrag = useCallback(() => {
    dragRef.current = null
    document.body.style.userSelect = ''
    document.removeEventListener('pointermove', onPointerMove)
    document.removeEventListener('pointerup', endDrag)
    document.removeEventListener('pointercancel', endDrag)
  }, [onPointerMove])

  const startDrag = (event) => {
    const box = panelRef.current.getBoundingClientRect()
    dragRef.current = { x: event.clientX - box.left, y: event.clientY - box.top }
    document.body.style.userSelect = 'none'
    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', endDrag)
    // Without this a cancelled pointer leaves userSelect:none set app-wide.
    document.addEventListener('pointercancel', endDrag)
  }

  useEffect(() => endDrag, [endDrag])

  if (!open) return null

  return (
    <div
      className="console-log"
      dir="ltr"
      ref={panelRef}
      style={{ insetInlineStart: position.x, top: position.y }}
    >
      <div className="console-log__header" onPointerDown={startDrag}>
        <span className="console-log__title">
          <bdi>{shell.consoleLogs}</bdi>
        </span>
        <button
          type="button"
          className="console-log__close"
          onClick={() => onOpenChange(false)}
          aria-label={shell.close}
        >
          <X size={14} strokeWidth={2.25} />
        </button>
      </div>

      <div className="console-log__body" ref={bodyRef}>
        {entries.map((entry, index) => (
          <div key={index} className={`console-log__row console-log__row--${entry.level}`}>
            <pre className="console-log__text">{entry.text}</pre>
            <span className="console-log__location">{entry.location}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
